/**
 * ZIYA global interactions — ports every non-cart behaviour from the
 * original ziya-shapewear.jsx: mobile nav, sticky header state, scroll
 * reveal (useReveal.jsx), product gallery + variant selection, accordions,
 * shop quick-add, results filters, cookie consent, newsletter popup and
 * the chatbot widget.
 */
(function () {
  'use strict';

  var prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ---------------------------- storage helpers ---------------------------- */
  function readJSON(key) {
    try {
      var raw = window.localStorage.getItem(key);
      return raw ? JSON.parse(raw) : null;
    } catch (e) {
      return null;
    }
  }
  function writeJSON(key, value) {
    try {
      window.localStorage.setItem(key, JSON.stringify(value));
    } catch (e) { /* best-effort persistence */ }
  }

  /* ----------------------------- visit clock ------------------------------ */
  // Popup delays count from the start of the visit, not from each page load,
  // so a visitor who clicks through a few pages still sees them on time.
  var SESSION_START_KEY = 'ziya-visit-started';
  var visitStart = (function () {
    try {
      var stored = parseInt(window.sessionStorage.getItem(SESSION_START_KEY), 10);
      if (stored && stored <= Date.now()) return stored;
      window.sessionStorage.setItem(SESSION_START_KEY, String(Date.now()));
    } catch (e) { /* storage blocked — fall back to this page load */ }
    return Date.now();
  })();
  var MIN_DELAY_AFTER_LOAD_MS = 1500; // never pop up the instant a page appears

  // Runs fn once the visitor has scrolled (not just loaded) to the given
  // share of the page, and never sooner than MIN_DELAY_AFTER_LOAD_MS.
  function afterScrollDepth(share, fn) {
    var done = false;
    var queued = false;
    function check() {
      queued = false;
      if (done) return;
      var doc = document.documentElement;
      var reached = window.scrollY > 0 && (window.scrollY + window.innerHeight) >= doc.scrollHeight * share;
      if (!reached) return;
      done = true;
      window.removeEventListener('scroll', onScroll);
      afterVisitTime(0, fn);
    }
    function onScroll() {
      if (!queued) { queued = true; window.requestAnimationFrame(check); }
    }
    window.addEventListener('scroll', onScroll, { passive: true });
  }

  function afterVisitTime(ms, fn) {
    var wait = Math.max(ms - (Date.now() - visitStart), MIN_DELAY_AFTER_LOAD_MS);
    return window.setTimeout(fn, wait);
  }

  /* ---------------------------- popup manager ----------------------------- */
  // One popup at a time. Popups register themselves, then *request* to be
  // shown; requests queue by priority (promo before cookie consent) and only
  // open when nothing else is open and the visitor isn't busy in the cart,
  // menu or chat. Closing one lets the next queued popup in.
  var Popups = (function () {
    var registry = {};
    var queue = [];
    var current = null;
    var retryTimer = null;
    var lastFocus = null;

    function isBusy() {
      var chat = document.querySelector('[data-chatbot-panel]');
      if (chat && !chat.hidden) return true;
      if (document.querySelector('[data-cart-drawer].is-open')) return true;
      if (document.querySelector('[data-mobile-nav].is-open')) return true;
      return false;
    }

    function announce() {
      if (current) document.documentElement.setAttribute('data-popup-open', current);
      else document.documentElement.removeAttribute('data-popup-open');
      document.dispatchEvent(new CustomEvent('ziya:popup-change', { detail: { open: current } }));
    }

    function show(name) {
      current = name;
      lastFocus = document.activeElement;
      registry[name].open();
      announce();
    }

    function pump() {
      if (retryTimer) { window.clearTimeout(retryTimer); retryTimer = null; }
      if (current || !queue.length) return;
      if (isBusy()) { retryTimer = window.setTimeout(pump, 1000); return; }
      show(queue.shift());
    }

    return {
      register: function (name, priority, handlers) {
        registry[name] = { priority: priority, open: handlers.open, close: handlers.close, escapable: handlers.escapable };
      },
      request: function (name) {
        if (!registry[name] || current === name || queue.indexOf(name) !== -1) return;
        queue.push(name);
        queue.sort(function (a, b) { return registry[a].priority - registry[b].priority; });
        pump();
      },
      // Visitor asked for this popup (e.g. the footer cookie link): show it now,
      // closing whatever automatic popup was open.
      openNow: function (name) {
        if (!registry[name]) return;
        queue = queue.filter(function (n) { return n !== name; });
        if (current === name) return;
        if (current) registry[current].close('replaced');
        show(name);
      },
      // Every popup calls this once it has hidden itself.
      released: function (name) {
        queue = queue.filter(function (n) { return n !== name; });
        if (current !== name) return;
        current = null;
        announce();
        if (lastFocus && document.contains(lastFocus) && lastFocus.focus) {
          try { lastFocus.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        }
        lastFocus = null;
        window.setTimeout(pump, 600); // a short breath between two popups
      },
      current: function () { return current; },
      escape: function () {
        if (!current) return;
        var entry = registry[current];
        if (!entry.escapable || entry.escapable()) entry.close('escape');
      },
      pump: pump
    };
  })();
  window.ZIYA = window.ZIYA || {};
  window.ZIYA.popups = Popups;

  document.addEventListener('keydown', function (e) {
    if (e.key === 'Escape' || e.key === 'Esc') Popups.escape();
  });

  // Fade a popup element in/out (.is-entering / .is-leaving drive the CSS).
  function revealEl(el) {
    el.hidden = false;
    el.classList.remove('is-leaving');
    if (prefersReducedMotion) return;
    el.classList.add('is-entering');
    // two frames so the starting state is painted before transitioning
    window.requestAnimationFrame(function () {
      window.requestAnimationFrame(function () { el.classList.remove('is-entering'); });
    });
  }
  function concealEl(el, done) {
    if (prefersReducedMotion || el.hidden) { el.hidden = true; if (done) done(); return; }
    el.classList.add('is-leaving');
    window.setTimeout(function () {
      el.hidden = true;
      el.classList.remove('is-leaving');
      if (done) done();
    }, 320);
  }

  /* ------------------------ chat button vs. popups ------------------------- */
  // The chat button stays visible but slides up out of the way whenever an
  // open popup or the cookie card would otherwise sit underneath it.
  function initChatClearance() {
    var bubble = document.querySelector('[data-chatbot-open]');
    if (!bubble) return;
    var root = document.documentElement;

    function openCard() {
      var name = Popups.current();
      if (name === 'promo') return document.querySelector('[data-newsletter-card]');
      if (name === 'cookie') return document.querySelector('[data-cookie-banner]');
      return null;
    }

    function update() {
      var card = openCard();
      if (!card || card.hidden) { root.style.setProperty('--chat-lift', '0px'); return; }
      var c = card.getBoundingClientRect();
      // offset* ignores the lift transform, so this is the button's resting spot
      var b = { left: bubble.offsetLeft, top: bubble.offsetTop };
      b.right = b.left + bubble.offsetWidth;
      b.bottom = b.top + bubble.offsetHeight;
      var overlapsX = b.left < c.right && b.right > c.left;
      var overlapsY = b.top < c.bottom && b.bottom > c.top;
      root.style.setProperty('--chat-lift', overlapsX && overlapsY ? Math.ceil(b.bottom - c.top + 12) + 'px' : '0px');
    }

    document.addEventListener('ziya:popup-change', function () {
      update();
      // measure again once the entrance animation has settled
      window.setTimeout(update, 360);
    });
    window.addEventListener('resize', update);
    document.addEventListener('ziya:popup-resize', update);
  }

  /* ------------------------------ mobile nav ------------------------------- */
  function initMobileNav() {
    var nav = document.querySelector('[data-mobile-nav]');
    if (!nav) return;
    document.querySelectorAll('[data-mobile-nav-open]').forEach(function (btn) {
      btn.addEventListener('click', function () { nav.classList.add('is-open'); });
    });
    document.querySelectorAll('[data-mobile-nav-close]').forEach(function (btn) {
      btn.addEventListener('click', function () { nav.classList.remove('is-open'); });
    });
  }

  /* ---------------------------- sticky header state ------------------------ */
  function initHeaderScroll() {
    var header = document.querySelector('[data-site-header]');
    if (!header) return;
    var onHero = header.getAttribute('data-on-hero') === 'true';
    function update() {
      var scrolled = window.scrollY > 24;
      header.classList.toggle('is-scrolled', scrolled);
      header.classList.toggle('on-hero', onHero && !scrolled);
    }
    update();
    window.addEventListener('scroll', update, { passive: true });
  }

  /* ------------------------------ scroll reveal ----------------------------- */
  function initReveal() {
    var els = document.querySelectorAll('[data-reveal]');
    els.forEach(function (el) { el.classList.add('reveal'); });
    if (prefersReducedMotion || !('IntersectionObserver' in window)) {
      els.forEach(function (el) { el.classList.add('is-visible'); });
      return;
    }
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (entry.isIntersecting) {
            entry.target.classList.add('is-visible');
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.12, rootMargin: '0px 0px -6% 0px' }
    );
    els.forEach(function (el) { observer.observe(el); });
  }

  /* -------------------------------- accordion ------------------------------- */
  function initAccordions() {
    document.querySelectorAll('[data-accordion-toggle]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var item = btn.closest('[data-accordion-item]');
        var panel = item.querySelector('[data-accordion-panel]');
        var isOpen = item.classList.contains('is-open');
        // Only one open at a time within the same accordion list, matching
        // the original's single `open` index state.
        var list = item.parentElement;
        list.querySelectorAll('[data-accordion-item].is-open').forEach(function (openItem) {
          openItem.classList.remove('is-open');
          openItem.querySelector('[data-accordion-panel]').hidden = true;
          var openBtn = openItem.querySelector('[data-accordion-toggle]');
          if (openBtn) openBtn.setAttribute('aria-expanded', 'false');
        });
        if (!isOpen) {
          item.classList.add('is-open');
          panel.hidden = false;
          btn.setAttribute('aria-expanded', 'true');
        }
      });
    });
  }

  /* ------------------------------ product gallery ---------------------------- */
  // .gallery-thumbs / .gallery-main-col / .gallery-mobile-wrap are the real
  // grid children (required for the desktop 3-column layout); each one
  // separately nests a nearly-identical [data-gallery-for-color] block per
  // colour. So "the active colour's gallery" is really up to three sibling
  // elements sharing the same data-gallery-for-color value, not one — every
  // helper below operates on all of them together by colour value, never on
  // a single DOM subtree.
  function initProductGallery(root) {
    function colorScopes(colorValue) {
      return root.querySelectorAll('[data-gallery-for-color="' + CSS.escape(colorValue) + '"]');
    }

    function showColorGallery(colorValue) {
      root.querySelectorAll('[data-gallery-for-color]').forEach(function (block) {
        block.hidden = block.getAttribute('data-gallery-for-color') !== colorValue;
      });
    }

    function selectImageIndex(colorValue, index) {
      colorScopes(colorValue).forEach(function (scope) {
        scope.querySelectorAll('[data-gallery-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-index') === String(index));
        });
        scope.querySelectorAll('[data-gallery-mobile-thumb]').forEach(function (t) {
          t.classList.toggle('is-active', t.getAttribute('data-index') === String(index));
        });
        scope.querySelectorAll('[data-gallery-main-image]').forEach(function (img) {
          img.hidden = img.getAttribute('data-index') !== String(index);
        });
        var dots = scope.querySelectorAll('.gallery-dot');
        dots.forEach(function (dot, i) { dot.classList.toggle('is-active', i === index); });
      });
    }

    root.addEventListener('click', function (e) {
      var thumb = e.target.closest('[data-gallery-thumb]');
      if (thumb) {
        var block = thumb.closest('[data-gallery-for-color]');
        var colorValue = block ? block.getAttribute('data-gallery-for-color') : null;
        selectImageIndex(colorValue, parseInt(thumb.getAttribute('data-index'), 10));
      }
      var mobileThumb = e.target.closest('[data-gallery-mobile-thumb]');
      if (mobileThumb) {
        var mBlock = mobileThumb.closest('[data-gallery-for-color]');
        var mColorValue = mBlock ? mBlock.getAttribute('data-gallery-for-color') : null;
        var index = parseInt(mobileThumb.getAttribute('data-index'), 10);
        var scroller = mBlock ? mBlock.querySelector('[data-gallery-scroller]') : null;
        if (scroller) {
          var itemWidth = scroller.clientWidth;
          scroller.scrollTo({ left: index * itemWidth, behavior: 'smooth' });
        }
        selectImageIndex(mColorValue, index);
      }
    });

    root.querySelectorAll('[data-gallery-zoom]').forEach(function (zoom) {
      zoom.addEventListener('mousemove', function (e) {
        var rect = zoom.getBoundingClientRect();
        var x = ((e.clientX - rect.left) / rect.width) * 100;
        var y = ((e.clientY - rect.top) / rect.height) * 100;
        var img = zoom.querySelector('[data-gallery-main-image]:not([hidden]) .gallery-img-zoom');
        if (img) img.style.transformOrigin = x + '% ' + y + '%';
      });
    });

    root.querySelectorAll('[data-gallery-scroller]').forEach(function (scroller) {
      scroller.addEventListener('scroll', function () {
        if (scroller.clientWidth === 0) return;
        var index = Math.round(scroller.scrollLeft / scroller.clientWidth);
        var block = scroller.closest('[data-gallery-for-color]');
        var colorValue = block ? block.getAttribute('data-gallery-for-color') : null;
        selectImageIndex(colorValue, index);
      });
    });

    return { showColorGallery: showColorGallery, selectImageIndex: selectImageIndex, colorScopes: colorScopes };
  }

  /* ------------------------------ variant selection --------------------------- */
  function formatMoney(cents) {
    // Falls back to a plain amount if Shopify.formatMoney/currency isn't
    // available; the price shown right after a swatch/size change is
    // re-derived from the variant JSON's already-formatted price string
    // where possible, so this is only a light fallback.
    return (cents / 100).toFixed(2);
  }

  function initProductForm() {
    var root = document.querySelector('[data-product-root]');
    if (!root) return;

    var productJsonEl = root.querySelector('[data-product-json]');
    if (!productJsonEl) return;
    var product = JSON.parse(productJsonEl.textContent);

    var gallery = initProductGallery(root);
    var variantInput = root.querySelector('[data-selected-variant-id]');
    var priceEl = root.querySelector('[data-purchase-price]');
    var colorLabelEl = root.querySelector('[data-selected-color-label]');
    var sizeLabelEl = root.querySelector('[data-selected-size-label]');
    var addButton = root.querySelector('[data-add-to-cart-button]');
    var addLabels = document.querySelectorAll('[data-add-to-cart-label]');
    var stickyName = document.querySelector('[data-sticky-name]');
    var stickyPrice = document.querySelector('[data-sticky-price]');
    var stickyButtons = document.querySelectorAll('.sticky-add-bar [type="submit"]');

    var colorIndex = -1;
    var sizeIndex = -1;
    (product.options || []).forEach(function (name, i) {
      var down = name.toLowerCase();
      if (down.indexOf('colour') > -1 || down.indexOf('color') > -1 || down.indexOf('kleur') > -1) {
        colorIndex = i;
      } else if (sizeIndex === -1) {
        sizeIndex = i;
      }
    });

    var initialVariantId = variantInput ? Number(variantInput.value) : null;
    var selected = {};
    var current = product.variants.find(function (v) { return v.id === initialVariantId; }) || product.variants[0];
    if (colorIndex > -1) selected[colorIndex] = current.options[colorIndex];
    if (sizeIndex > -1) selected[sizeIndex] = current.options[sizeIndex];
    var lastColor = colorIndex > -1 ? selected[colorIndex] : null;

    function findVariant() {
      return product.variants.find(function (v) {
        return Object.keys(selected).every(function (idx) { return v.options[idx] === selected[idx]; });
      });
    }

    function moneyString(amountCents) {
      // Reuse the server-rendered price string's currency formatting by
      // swapping only the digits, so symbol/decimal/thousands stay correct
      // for the shop's locale without needing Shopify.formatMoney here.
      if (!priceEl) return formatMoney(amountCents);
      var template = priceEl.getAttribute('data-money-format') || priceEl.textContent;
      var digits = formatMoney(amountCents).replace('.', ',');
      return template.replace(/[\d.,]+/, digits);
    }

    function render() {
      var variant = findVariant();
      if (!variant) return;

      if (variantInput) variantInput.value = variant.id;
      if (priceEl) {
        if (!priceEl.getAttribute('data-money-format')) priceEl.setAttribute('data-money-format', priceEl.textContent);
        priceEl.textContent = moneyString(variant.price);
      }
      if (colorIndex > -1 && colorLabelEl) colorLabelEl.textContent = selected[colorIndex];
      if (sizeIndex > -1 && sizeLabelEl) sizeLabelEl.textContent = selected[sizeIndex];

      var available = !!variant.available;
      addLabels.forEach(function (label) {
        label.textContent = available ? label.getAttribute('data-in-stock-text') || label.textContent : label.getAttribute('data-sold-out-text') || label.textContent;
      });
      if (addButton) addButton.disabled = !available;
      stickyButtons.forEach(function (btn) { btn.disabled = !available; });

      if (stickyName) {
        stickyName.textContent = product.title + (colorIndex > -1 ? ' — ' + selected[colorIndex] : '');
      }
      if (stickyPrice) stickyPrice.textContent = moneyString(variant.price);

      // swatch / chip active states + per-combination sold-out state.
      // Colour swatches stay clickable regardless of stock — they only
      // switch which photos/variant are shown, matching the original
      // design where colour is always browsable. Only size chips reflect
      // per-combination availability, since "Add to Bag" already covers
      // the actual stock gate.
      root.querySelectorAll('[data-option-select]').forEach(function (btn) {
        var idx = parseInt(btn.getAttribute('data-option-index'), 10);
        var value = btn.getAttribute('data-option-value');
        var isSelected = selected[idx] === value;
        btn.classList.toggle('is-selected', isSelected);
        var isSwatch = btn.classList.contains('swatch');
        if (isSwatch) btn.setAttribute('aria-pressed', String(isSelected));

        if (isSwatch) {
          btn.classList.remove('is-soldout');
          btn.disabled = false;
          return;
        }

        var hypothetical = Object.assign({}, selected);
        hypothetical[idx] = value;
        var matchingAny = product.variants.some(function (v) {
          return Object.keys(hypothetical).every(function (i) { return v.options[i] === hypothetical[i]; }) && v.available;
        });
        btn.classList.toggle('is-soldout', !matchingAny);
        btn.disabled = !matchingAny;
      });

      if (colorIndex > -1) {
        gallery.showColorGallery(selected[colorIndex]);
        if (selected[colorIndex] !== lastColor) {
          lastColor = selected[colorIndex];
          gallery.selectImageIndex(selected[colorIndex], 0);
          gallery.colorScopes(selected[colorIndex]).forEach(function (scope) {
            var activeScroller = scope.querySelector('[data-gallery-scroller]');
            if (activeScroller) activeScroller.scrollTo({ left: 0 });
          });
        }

        document.querySelectorAll('[data-result-teaser-for-color]').forEach(function (block) {
          block.hidden = block.getAttribute('data-result-teaser-for-color') !== selected[colorIndex];
        });
      }
    }

    root.addEventListener('click', function (e) {
      var opt = e.target.closest('[data-option-select]');
      if (!opt || opt.disabled) return;
      var idx = parseInt(opt.getAttribute('data-option-index'), 10);
      selected[idx] = opt.getAttribute('data-option-value');
      render();
    });

    render();
  }

  /* -------------------------------- shop quick add ----------------------------- */
  function initQuickAdd() {
    document.querySelectorAll('[data-quick-add-open]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.shop-two-card');
        card.querySelector('[data-quick-add-panel]').hidden = false;
        card.querySelector('[data-quick-add-default-actions]').hidden = true;
      });
    });
    document.querySelectorAll('[data-quick-add-close]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var card = btn.closest('.shop-two-card');
        card.querySelector('[data-quick-add-panel]').hidden = true;
        card.querySelector('[data-quick-add-default-actions]').hidden = false;
      });
    });
    document.querySelectorAll('[data-quick-add-size]').forEach(function (chip) {
      chip.addEventListener('click', function () {
        if (chip.disabled) return;
        var panel = chip.closest('[data-quick-add-panel]');
        panel.querySelectorAll('[data-quick-add-size]').forEach(function (c) { c.classList.remove('is-selected'); });
        chip.classList.add('is-selected');
        var confirmBtn = panel.querySelector('[data-quick-add-confirm]');
        confirmBtn.disabled = false;
        confirmBtn.setAttribute('data-variant-id', chip.getAttribute('data-variant-id'));
      });
    });
    document.querySelectorAll('[data-quick-add-confirm]').forEach(function (btn) {
      btn.addEventListener('click', function () {
        var variantId = btn.getAttribute('data-variant-id');
        if (!variantId || !window.ZiyaCart) return;
        window.ZiyaCart.addVariant(variantId, 1);
      });
    });
  }

  /* ------------------------------- results filters ------------------------------ */
  function initResultsFilters() {
    var filterBar = document.querySelector('[data-results-filters]');
    if (!filterBar) return;
    filterBar.addEventListener('click', function (e) {
      var chip = e.target.closest('[data-filter-chip]');
      if (!chip) return;
      filterBar.querySelectorAll('[data-filter-chip]').forEach(function (c) { c.classList.remove('is-active'); });
      chip.classList.add('is-active');
      var value = chip.getAttribute('data-filter-value');
      document.querySelectorAll('[data-result-card]').forEach(function (card) {
        var show = value === 'All' || card.getAttribute('data-result-size') === value;
        card.hidden = !show;
      });
    });
  }

  /* ------------------------------- cookie consent -------------------------------- */
  // Every choice is passed to Shopify's Customer Privacy API. Until the
  // visitor accepts, Shopify itself keeps analytics and app pixels (such as
  // the Facebook pixel) switched off, so the card can wait a minute without
  // anything being tracked in the meantime. The choice is also kept in
  // localStorage for any theme code that needs it.
  var COOKIE_KEY = 'ziya-cookie-consent';
  var DEFAULT_CONSENT = { necessary: true, preferences: false, analytics: false, marketing: false };
  var COOKIE_DELAY_MS = 60 * 1000;
  var COOKIE_SETTINGS_LINK = 'a[href*="/pages/cookie-instellingen"], a[href*="/pages/cookie-settings"], [data-open-cookie-settings]';

  var Consent = (function () {
    var apiCallbacks = [];
    var api = null;
    var listeners = [];

    function withApi(fn) {
      if (api) { fn(api); return; }
      apiCallbacks.push(fn);
    }

    function flush(value) {
      api = value;
      var queued = apiCallbacks;
      apiCallbacks = [];
      queued.forEach(function (fn) { fn(api); });
    }

    function load() {
      if (window.Shopify && window.Shopify.customerPrivacy) { flush(window.Shopify.customerPrivacy); return; }
      if (!window.Shopify || typeof window.Shopify.loadFeatures !== 'function') { flush(null); return; }
      try {
        window.Shopify.loadFeatures([{ name: 'consent-tracking-api', version: '0.1' }], function (error) {
          flush(error ? null : (window.Shopify.customerPrivacy || null));
        });
      } catch (e) { flush(null); }
      // Never leave the banner waiting forever on a slow or blocked script.
      window.setTimeout(function () { if (apiCallbacks.length) flush(window.Shopify.customerPrivacy || null); }, 8000);
    }

    // The visitor's current choice, or null when they haven't chosen yet.
    function current(privacy) {
      if (privacy && typeof privacy.currentVisitorConsent === 'function') {
        var c = privacy.currentVisitorConsent() || {};
        if (c.analytics === '' && c.marketing === '' && c.preferences === '') return null;
        return { necessary: true, preferences: c.preferences === 'yes', analytics: c.analytics === 'yes', marketing: c.marketing === 'yes' };
      }
      return readJSON(COOKIE_KEY);
    }

    function needsBanner(privacy) {
      if (privacy && typeof privacy.shouldShowBanner === 'function') return privacy.shouldShowBanner();
      return !readJSON(COOKIE_KEY);
    }

    function save(value, done) {
      writeJSON(COOKIE_KEY, value);
      withApi(function (privacy) {
        var finish = function () {
          listeners.forEach(function (fn) { try { fn(value); } catch (e) { /* keep going */ } });
          document.dispatchEvent(new CustomEvent('ziya:consent', { detail: value }));
          if (done) done();
        };
        if (!privacy || typeof privacy.setTrackingConsent !== 'function') { finish(); return; }
        var payload = { analytics: !!value.analytics, marketing: !!value.marketing, preferences: !!value.preferences };
        if (typeof privacy.saleOfDataRegion === 'function' && privacy.saleOfDataRegion()) payload.sale_of_data = !!value.marketing;
        try { privacy.setTrackingConsent(payload, finish); } catch (e) { finish(); }
      });
    }

    return {
      load: load,
      withApi: withApi,
      current: current,
      needsBanner: needsBanner,
      save: save,
      // For future pixels added to the theme: run fn only once (and as soon
      // as) the visitor has allowed this purpose.
      whenAllowed: function (purpose, fn) {
        withApi(function (privacy) {
          var now = current(privacy);
          if (now && now[purpose]) { fn(); return; }
          listeners.push(function (value) { if (value[purpose]) fn(); });
        });
      }
    };
  })();
  window.ZIYA.consent = { whenAllowed: Consent.whenAllowed };

  function fillCategoryInputs(container, value) {
    container.querySelectorAll('[data-cookie-cat]').forEach(function (input) {
      input.checked = !!(value && value[input.getAttribute('data-cookie-cat')]);
    });
  }
  function readCategoryInputs(container) {
    var value = Object.assign({}, DEFAULT_CONSENT);
    container.querySelectorAll('[data-cookie-cat]').forEach(function (input) {
      value[input.getAttribute('data-cookie-cat')] = input.checked;
    });
    return value;
  }

  function initCookieBanner() {
    var banner = document.querySelector('[data-cookie-banner]');
    Consent.load();
    if (!banner) return;
    document.documentElement.classList.add('ziya-consent-ready');

    var simple = banner.querySelector('[data-cookie-banner-simple]');
    var manage = banner.querySelector('[data-cookie-banner-manage]');
    var closeBtn = banner.querySelector('[data-cookie-close]');
    var openedByVisitor = false;

    function showView(view) {
      simple.hidden = view !== 'simple';
      manage.hidden = view !== 'manage';
      document.dispatchEvent(new CustomEvent('ziya:popup-resize'));
    }

    Popups.register('cookie', 2, {
      open: function () {
        revealEl(banner);
        try { banner.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
      },
      close: function () { hide(); },
      // Esc only works when the card was opened on purpose or a choice
      // already exists — a first-time visitor has to pick an option.
      escapable: function () { return openedByVisitor; }
    });

    function hide() {
      concealEl(banner, function () {
        showView('simple');
        Popups.released('cookie');
      });
    }

    function choose(value) {
      Consent.save(value);
      hide();
    }

    banner.querySelector('[data-cookie-accept]').addEventListener('click', function () {
      choose({ necessary: true, preferences: true, analytics: true, marketing: true });
    });
    banner.querySelector('[data-cookie-reject]').addEventListener('click', function () {
      choose(Object.assign({}, DEFAULT_CONSENT));
    });
    banner.querySelector('[data-cookie-manage]').addEventListener('click', function () { showView('manage'); });
    banner.querySelector('[data-cookie-back]').addEventListener('click', function () { showView('simple'); });
    banner.querySelector('[data-cookie-save-selection]').addEventListener('click', function () {
      choose(readCategoryInputs(manage));
    });
    closeBtn.addEventListener('click', hide);

    // "Cookie-instellingen" links (footer menu) open the card right away, on
    // the detailed view, showing the visitor's current choice.
    document.addEventListener('click', function (e) {
      var link = e.target.closest && e.target.closest(COOKIE_SETTINGS_LINK);
      if (!link) return;
      e.preventDefault();
      Consent.withApi(function (privacy) {
        var now = Consent.current(privacy);
        openedByVisitor = true;
        closeBtn.hidden = false;
        fillCategoryInputs(manage, now || DEFAULT_CONSENT);
        showView('manage');
        Popups.openNow('cookie');
      });
    });

    // Automatic first-visit card — skipped on the cookie settings page itself.
    if (document.querySelector('[data-cookie-settings-page]')) return;
    Consent.withApi(function (privacy) {
      if (!Consent.needsBanner(privacy) || Consent.current(privacy)) return;
      afterVisitTime(COOKIE_DELAY_MS, function () {
        if (Consent.current(privacy)) return; // chose meanwhile, e.g. via the footer link
        Popups.request('cookie');
      });
    });
  }

  function initCookieSettingsPage() {
    var page = document.querySelector('[data-cookie-settings-page]');
    if (!page) return;
    Consent.withApi(function (privacy) {
      fillCategoryInputs(page, Consent.current(privacy) || DEFAULT_CONSENT);
    });
    page.querySelector('[data-cookie-settings-save]').addEventListener('click', function () {
      Consent.save(readCategoryInputs(page), function () {
        page.querySelector('[data-cookie-settings-saved]').hidden = false;
      });
    });
  }

  /* ------------------------------- newsletter popup -------------------------------- */
  // No discount is offered or displayed here — this popup exists purely to
  // grow the real Shopify customer/marketing list (see newsletter-popup.liquid
  // for the {% form 'customer' %} that actually creates the Customer record).
  //
  // Storage keys are versioned ("-insider-v2"): the old 10%-discount popup
  // used plain 'ziya-newsletter' / 'ziya-newsletter-dismissed' with no
  // expiry, so anyone who had already dismissed or claimed THAT popup would
  // have that state permanently block this one from ever showing, even
  // though it's a different offer. Bump this suffix again if the popup's
  // offer changes materially in the future for the same reason.
  var NEWSLETTER_KEY = 'ziya-newsletter-insider-v2';
  var NEWSLETTER_DISMISSED_KEY = 'ziya-newsletter-insider-v2-dismissed';
  var DISMISS_SUPPRESS_DAYS = 7; // closed without signing up — ask again later
  var SIGNUP_SUPPRESS_DAYS = 365; // already signed up — don't re-pester
  var DAY_MS = 24 * 60 * 60 * 1000;
  var EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

  function pushAnalyticsEvent(name) {
    try {
      window.dataLayer = window.dataLayer || [];
      window.dataLayer.push({ event: name });
    } catch (e) { /* analytics must never break the popup itself */ }
  }

  var PROMO_SCROLL_DEPTH = 0.5; // show the promo once the visitor has scrolled halfway down the page
  var NEWSLETTER_SUBMIT_TIMEOUT_MS = 10000; // safety net if the hidden iframe never fires 'load'

  function initNewsletterPopup() {
    var popup = document.querySelector('[data-newsletter-popup]');
    if (!popup) return;
    var form = popup.closest('form') || popup.querySelector('form');
    var iframe = form ? form.querySelector('[data-newsletter-iframe]') : null;
    var i18nEl = document.querySelector('[data-newsletter-i18n]');
    var i18n = i18nEl ? JSON.parse(i18nEl.textContent) : {};
    var formState = popup.querySelector('[data-newsletter-form-state]');
    var successState = popup.querySelector('[data-newsletter-success-state]');
    var submitBtn = popup.querySelector('[data-newsletter-submit]');
    var errorEl = popup.querySelector('[data-newsletter-error]');

    // No-JS / no-iframe fallback: Liquid's form.posted_successfully? on the
    // page Shopify redirects back to after a real top-level submission (see
    // newsletter-popup.liquid). With JS enabled this path is no longer
    // normally reached, since submissions target the hidden iframe below
    // instead — but it's kept so the popup still degrades correctly without
    // JavaScript rather than breaking.
    if (i18n.justSubscribed) {
      writeJSON(NEWSLETTER_KEY, { claimedAt: Date.now() });
      pushAnalyticsEvent('newsletter_popup_success');
    }

    var claimed = readJSON(NEWSLETTER_KEY);
    var dismissed = readJSON(NEWSLETTER_DISMISSED_KEY);
    var claimedActive = !!claimed && (Date.now() - claimed.claimedAt) < SIGNUP_SUPPRESS_DAYS * DAY_MS;
    var dismissedActive = !!dismissed && (Date.now() - dismissed.dismissedAt) < DISMISS_SUPPRESS_DAYS * DAY_MS;

    var card = popup.querySelector('[data-newsletter-card]');

    Popups.register('promo', 1, {
      open: function () {
        revealEl(popup);
        try { card.focus({ preventScroll: true }); } catch (e) { /* ignore */ }
        pushAnalyticsEvent('newsletter_popup_view');
      },
      close: function () { close(); }
    });

    function close() {
      if (popup.hidden) return;
      // Only a real close of the FORM (not the post-signup success view)
      // counts as "dismissed without signing up".
      if (formState.hidden === false) writeJSON(NEWSLETTER_DISMISSED_KEY, { dismissedAt: Date.now() });
      concealEl(popup, function () { Popups.released('promo'); });
    }
    popup.querySelector('[data-newsletter-close]').addEventListener('click', close);
    // Any click outside the card closes it. On mobile there is no dimmed
    // backdrop (the page stays usable), so this listens on the document.
    document.addEventListener('click', function (e) {
      if (popup.hidden || popup.classList.contains('is-leaving')) return;
      if (card.contains(e.target)) return;
      close();
    });

    if (!popup.hidden) {
      // No-JS fallback page after a signup: the success view is already
      // showing, so let the manager know it's occupied.
      Popups.openNow('promo');
    } else if (!claimedActive && !dismissedActive) {
      // Priority: an active claimed state always wins, regardless of any
      // dismissed record that might also exist. Only once the visitor has
      // actually scrolled halfway down the page (so it never covers the hero
      // and its button on arrival), and only when nothing else is open (see
      // the popup manager).
      afterScrollDepth(PROMO_SCROLL_DEPTH, function () { Popups.request('promo'); });
    }

    // Submit the real, CAPTCHA-verified request into the hidden iframe
    // instead of the top-level window, so a genuine native form submission
    // (required for Shopify's own automatic hCaptcha handling — see the
    // file-level comment in newsletter-popup.liquid) never navigates the
    // visitor away from the page they're on.
    if (form && iframe) {
      form.target = 'newsletter-popup-target';
    }

    var submissionInFlight = false;
    var submissionTimer = null;

    function setSubmitting(isSubmitting) {
      if (!submitBtn) return;
      submitBtn.disabled = isSubmitting;
      submitBtn.textContent = isSubmitting ? i18n.submitting : i18n.submitLabel;
    }

    function finishSubmission(success) {
      if (!submissionInFlight) return; // already handled (or never started)
      submissionInFlight = false;
      if (submissionTimer) { window.clearTimeout(submissionTimer); submissionTimer = null; }
      setSubmitting(false);

      if (success) {
        errorEl.hidden = true;
        formState.hidden = true;
        successState.hidden = false;
        successState.querySelector('[data-newsletter-success-title]').textContent = i18n.successTitle;
        successState.querySelector('[data-newsletter-success-text]').textContent = i18n.successText;
        writeJSON(NEWSLETTER_KEY, { claimedAt: Date.now() });
        pushAnalyticsEvent('newsletter_popup_success');
        // Premium touch: close smoothly on its own after the visitor has had
        // a moment to read the confirmation — they never leave the page.
        window.setTimeout(close, 2500);
      } else {
        errorEl.textContent = i18n.submitError;
        errorEl.hidden = false;
      }
    }

    if (iframe) {
      iframe.addEventListener('load', function () {
        if (!submissionInFlight) return; // the iframe's initial blank load, not a real submission
        var success = false;
        try {
          // Shopify appends this exact query param on a successful native
          // customer-form submission (confirmed against this store's real
          // redirect behavior).
          success = /[?&]customer_posted=true(&|#|$)/.test(iframe.contentWindow.location.href);
        } catch (e) {
          // Cross-origin or otherwise inaccessible — can't confirm success.
          success = false;
        }
        finishSubmission(success);
      });
    }

    if (form) {
      form.addEventListener('submit', function (e) {
        var input = popup.querySelector('[data-newsletter-email]');
        var value = (input.value || '').trim();

        if (!EMAIL_RE.test(value)) {
          e.preventDefault();
          errorEl.textContent = i18n.invalidEmail;
          errorEl.hidden = false;
          return;
        }
        errorEl.hidden = true;

        if (claimed) {
          // Already on the list from a previous session — no need to
          // submit again; just reassure them.
          e.preventDefault();
          formState.hidden = true;
          successState.hidden = false;
          successState.querySelector('[data-newsletter-success-title]').textContent = i18n.alreadyTitle;
          successState.querySelector('[data-newsletter-success-text]').textContent = i18n.alreadyText;
          return;
        }

        // Deliberately NOT calling e.preventDefault() here: this must stay a
        // genuine, native submit event for Shopify's own hCaptcha handling
        // to engage (see the file-level comment in newsletter-popup.liquid).
        // The only thing redirecting it away from a visible top-level
        // navigation is form.target, set above.
        if (!iframe) {
          // No iframe available for some reason — fall back to a normal
          // full navigation rather than leaving the visitor stuck. Signups
          // still work; they just lose the same-page UX this once.
          pushAnalyticsEvent('newsletter_popup_submit');
          return;
        }

        submissionInFlight = true;
        setSubmitting(true);
        pushAnalyticsEvent('newsletter_popup_submit');
        submissionTimer = window.setTimeout(function () { finishSubmission(false); }, NEWSLETTER_SUBMIT_TIMEOUT_MS);
      });
    }
  }

  /* ---------------------------------- chatbot ---------------------------------- */
  function initChatbot() {
    var bubble = document.querySelector('[data-chatbot-open]');
    var panel = document.querySelector('[data-chatbot-panel]');
    if (!bubble || !panel) return;

    bubble.addEventListener('click', function () { panel.hidden = !panel.hidden; });
    panel.querySelector('[data-chatbot-close]').addEventListener('click', function () { panel.hidden = true; });

    var messagesEl = panel.querySelector('[data-chatbot-messages]');
    var topicsEl = panel.querySelector('[data-chatbot-topics]');

    function addMessage(text, from) {
      var p = document.createElement('p');
      p.className = 'chat-msg chat-msg-' + from;
      p.textContent = text;
      messagesEl.appendChild(p);
    }

    topicsEl.addEventListener('click', function (e) {
      var topic = e.target.closest('[data-chatbot-topic]');
      if (!topic) return;
      addMessage(topic.textContent, 'user');
      addMessage(topic.getAttribute('data-answer'), 'bot');
      topicsEl.hidden = true;
    });

    var input = panel.querySelector('[data-chatbot-input]');
    var fallbackEl = document.querySelector('[data-chatbot-fallback-answer]');
    var fallbackAnswer = fallbackEl ? JSON.parse(fallbackEl.textContent) : '';

    function send() {
      var value = (input.value || '').trim();
      if (!value) return;
      addMessage(value, 'user');
      addMessage(fallbackAnswer, 'bot');
      input.value = '';
      topicsEl.hidden = true;
    }
    panel.querySelector('[data-chatbot-send]').addEventListener('click', send);
    input.addEventListener('keydown', function (e) { if (e.key === 'Enter') send(); });
  }

  /* ------------------------------ hero slideshow ------------------------------ */
  // Crossfades the homepage banner (image + its own headline/button). The
  // active dot's fill bar is a CSS animation lasting one interval; when it
  // ends the next slide is shown, so pausing (hover, focus, touch, hidden tab)
  // simply freezes the bar where it is and resumes from there. Visitors who
  // prefer reduced motion get no animation and therefore no auto-advance.
  // Slides after the first keep their image sources in data-* attributes
  // until shortly before they're needed.
  function initHeroSlideshow(root) {
    var section = root.matches && root.matches('[data-hero-slideshow]') ? root : root.querySelector('[data-hero-slideshow]');
    if (!section || section.__ziyaSlideshow) return;
    var slides = Array.prototype.slice.call(section.querySelectorAll('[data-hero-slide]'));
    var copies = Array.prototype.slice.call(section.querySelectorAll('[data-hero-copy]'));
    var dots = Array.prototype.slice.call(section.querySelectorAll('[data-hero-dot]'));
    if (slides.length < 2) return;

    var index = 0;
    var pausedBy = {};

    function hydrate(slide) {
      if (!slide || slide.__hydrated) return;
      slide.__hydrated = true;
      slide.querySelectorAll('[data-srcset]').forEach(function (el) {
        el.setAttribute('srcset', el.getAttribute('data-srcset'));
        el.removeAttribute('data-srcset');
      });
      slide.querySelectorAll('img[data-src]').forEach(function (img) {
        img.setAttribute('src', img.getAttribute('data-src'));
        img.removeAttribute('data-src');
      });
    }

    function setActive(list, i) {
      list.forEach(function (el, n) {
        var on = n === i;
        el.classList.toggle('is-active', on);
        if (on) el.removeAttribute('aria-hidden');
        else el.setAttribute('aria-hidden', 'true');
        el.querySelectorAll('a, button').forEach(function (a) {
          if (on) a.removeAttribute('tabindex');
          else a.setAttribute('tabindex', '-1');
        });
      });
    }

    function go(next) {
      next = (next + slides.length) % slides.length;
      hydrate(slides[next]);
      setActive(slides, next);
      setActive(copies, next);
      dots.forEach(function (dot, i) {
        if (i === next) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
        // restart the fill animation on the newly active dot
        var bar = dot.querySelector('[data-hero-bar]');
        if (bar && i === next) {
          bar.style.animation = 'none';
          void bar.offsetWidth;
          bar.style.animation = '';
        }
      });
      index = next;
      hydrate(slides[(index + 1) % slides.length]); // get the next one ready
    }

    function pause(reason, on) {
      pausedBy[reason] = on;
      var paused = Object.keys(pausedBy).some(function (k) { return pausedBy[k]; });
      section.classList.toggle('is-paused', paused);
    }

    dots.forEach(function (dot, i) {
      dot.addEventListener('click', function () { go(i); });
      dot.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var next = (i + (e.key === 'ArrowRight' ? 1 : -1) + dots.length) % dots.length;
        go(next);
        dots[next].focus();
      });
      var bar = dot.querySelector('[data-hero-bar]');
      if (bar) {
        bar.addEventListener('animationend', function () {
          if (i === index && !prefersReducedMotion) go(index + 1);
        });
      }
    });

    // Mouse hover pauses; on touch screens a finger on the photo pauses
    // until it is lifted.
    section.addEventListener('pointerenter', function (e) { if (e.pointerType === 'mouse') pause('hover', true); });
    section.addEventListener('pointerleave', function (e) { if (e.pointerType === 'mouse') pause('hover', false); });
    section.addEventListener('focusin', function () { pause('focus', true); });
    section.addEventListener('focusout', function (e) {
      if (!section.contains(e.relatedTarget)) pause('focus', false);
    });
    document.addEventListener('visibilitychange', function () { pause('hidden', document.hidden); });

    // Swipe on touch screens (horizontal only, so vertical scrolling is untouched).
    var track = section.querySelector('[data-hero-track]');
    var startX = null;
    var startY = null;
    track.addEventListener('touchstart', function (e) {
      startX = e.touches[0].clientX;
      startY = e.touches[0].clientY;
      pause('touch', true);
    }, { passive: true });
    function endTouch(e) {
      pause('touch', false);
      if (startX === null) return;
      var dx = e.changedTouches[0].clientX - startX;
      var dy = e.changedTouches[0].clientY - startY;
      startX = null;
      if (Math.abs(dx) > 40 && Math.abs(dx) > Math.abs(dy)) go(index + (dx < 0 ? 1 : -1));
    }
    track.addEventListener('touchend', endTouch, { passive: true });
    track.addEventListener('touchcancel', function () { startX = null; pause('touch', false); }, { passive: true });

    // Theme editor: selecting a slide block shows it and holds it still.
    section.addEventListener('shopify:block:select', function (e) {
      var i = slides.indexOf(e.target.closest('[data-hero-slide]'));
      pause('editor', true);
      if (i !== -1) go(i);
    });
    section.addEventListener('shopify:block:deselect', function () { pause('editor', false); });

    section.__ziyaSlideshow = true;
    section.classList.add('is-ready');
    // Load the second slide only after the page itself has finished loading.
    if (document.readyState === 'complete') hydrate(slides[1]);
    else window.addEventListener('load', function () { hydrate(slides[1]); });
  }

  /* ------------------------ sticky add-to-bag (mobile) ----------------------- */
  // Only slides in once the main "In winkelmand" button has scrolled out of
  // view, so it never covers the product photo or the button itself.
  function initStickyAddBar() {
    var bar = document.querySelector('[data-sticky-add-bar]');
    var main = document.querySelector('[data-add-to-cart-button]');
    if (!bar || !main) return;
    var queued = false;
    function update() {
      queued = false;
      // shown only once the button itself has scrolled up out of view
      bar.classList.toggle('is-shown', main.getBoundingClientRect().bottom < 0);
    }
    window.addEventListener('scroll', function () {
      if (!queued) { queued = true; window.requestAnimationFrame(update); }
    }, { passive: true });
    update();
  }

  /* ------------------------------------ init ------------------------------------ */
  initMobileNav();
  initHeaderScroll();
  initReveal();
  initAccordions();
  initProductForm();
  initQuickAdd();
  initResultsFilters();
  initCookieBanner();
  initCookieSettingsPage();
  initNewsletterPopup();
  initChatbot();
  initChatClearance();
  initHeroSlideshow(document);
  initStickyAddBar();
  document.addEventListener('shopify:section:load', function (e) { initHeroSlideshow(e.target); });
})();
